import { useTranslation } from "react-i18next";
import NewPage from "../../../../layouts/page/NewPage";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { convertBooleanToStatus } from "../../../../../utils/ConverterUtils";
import MenuConfig from "../../../../../constants/MenuConfig";
import Form from "./Form";
import { getId, sendToApi } from "../../../../../utils/ApiUtils";
import { useNavigate, useParams } from "react-router-dom";
import Api from "../../../../../constants/Api";
import { ApiError } from "../../../../components/Common/ApiError";
import axiosInstance from "../../../../../services/AxiosInstance";

const WhatsappBotNew = () => {
    const { t } = useTranslation();
    const [status, setStatus] = useState(true);
    const [entityData, setEntityData] = useState(null);
    const [loadingConfiguration, setLoadingConfiguration] = useState(false);
    const navigate = useNavigate();
    const { id } = useParams();

    useEffect(() => {
        if (id) return;
        let isCurrentRequest = true;
        setLoadingConfiguration(true);
        axiosInstance.get(`${Api.whatsapp.bot}/configuration`)
            .then(({ data }) => {
                if (isCurrentRequest && data) setEntityData(data);
            })
            .catch((error) => {
                if (isCurrentRequest) ApiError(error);
            })
            .finally(() => {
                if (isCurrentRequest) setLoadingConfiguration(false);
            });

        return () => { isCurrentRequest = false; };
    }, [id]);
    const pairedTimeWindowValidation = function (value) {
        const otherField = this.path === 'inactivityFollowUpStartTime'
            ? 'inactivityFollowUpEndTime'
            : 'inactivityFollowUpStartTime';
        const otherValue = this.parent[otherField];

        return !this.parent.inactivityFollowUpEnabled
            || ((!value && !otherValue) || (Boolean(value) && Boolean(otherValue)));
    };

    const formSchema = Yup.object().shape({
        name: Yup.string()
            .min(3, t("validation.min.generic", { field: t('name'), qty: 3 }))
            .required(t('validation.required.name')),
        description: Yup.string()
            .max(255, t('validation.max_length', { max: 255 }))
            .nullable(),
        systemPrompt: Yup.string().nullable(),
        communicationStyle: Yup.string().oneOf(['FRIENDLY', 'PROFESSIONAL', 'DIRECT', 'WELCOMING']).required(),
        productCatalogEnabled: Yup.boolean(),
        subscriptionPlansEnabled: Yup.boolean(),
        branchConsultationEnabled: Yup.boolean(),
        employeeConsultationEnabled: Yup.boolean(),
        appointmentBookingEnabled: Yup.boolean(),
        billingRemindersEnabled: Yup.boolean(),
        billingReminderIntervalDays: Yup.number().when('billingRemindersEnabled', {
            is: true,
            then: (schema) => schema.required(t('validation.required.value')).integer().min(1).max(365)
        }),
        billingReminderMaxAttempts: Yup.number().when('billingRemindersEnabled', {
            is: true,
            then: (schema) => schema.required(t('validation.required.value')).integer().min(1).max(10)
        }),
        billingReminderStartTime: Yup.string().when('billingRemindersEnabled', {
            is: true, then: (schema) => schema.required(t('validation.required.value'))
                .test('billing-time-order', t('chatbot_billing_time_order'), function (value) {
                    return !value || !this.parent.billingReminderEndTime || value < this.parent.billingReminderEndTime;
                })
        }),
        billingReminderEndTime: Yup.string().when('billingRemindersEnabled', {
            is: true, then: (schema) => schema.required(t('validation.required.value'))
                .test('billing-time-order', t('chatbot_billing_time_order'), function (value) {
                    return !value || !this.parent.billingReminderStartTime || value > this.parent.billingReminderStartTime;
                })
        }),
        billingReminderOptInConfirmed: Yup.boolean().when('billingRemindersEnabled', {
            is: true,
            then: (schema) => schema.oneOf([true], t('chatbot_billing_opt_in_required'))
        }),
        birthdayMessagesEnabled: Yup.boolean(),
        birthdayGiftEnabled: Yup.boolean(),
        birthdayGiftDescription: Yup.string().trim().max(200, t('validation.max_length', { max: 200 })).when(['birthdayMessagesEnabled', 'birthdayGiftEnabled'], {
            is: (enabled, giftEnabled) => Boolean(enabled && giftEnabled),
            then: (schema) => schema.required(t('validation.required.value'))
        }),
        birthdayOptInConfirmed: Yup.boolean().when('birthdayMessagesEnabled', {
            is: true,
            then: (schema) => schema.oneOf([true], t('chatbot_birthday_opt_in_required'))
        }),
        appointmentRemindersEnabled: Yup.boolean(),
        appointmentSoonReminderEnabled: Yup.boolean(),
        appointmentCreatedConfirmationEnabled: Yup.boolean(),
        serviceFeedbackEnabled: Yup.boolean(),
        appointmentReminderLeadHours: Yup.number().when('appointmentRemindersEnabled', {
            is: true,
            then: (schema) => schema.required(t('validation.required.value')).integer().min(1).max(72)
        }),
        appointmentReminderStartTime: Yup.string().when(['appointmentRemindersEnabled', 'appointmentCreatedConfirmationEnabled'], {
            is: (reminders, created) => Boolean(reminders || created), then: (schema) => schema.required(t('validation.required.value'))
                .test('appointment-reminder-time-order', t('chatbot_billing_time_order'), function (value) {
                    return !value || !this.parent.appointmentReminderEndTime || value < this.parent.appointmentReminderEndTime;
                })
        }),
        appointmentReminderEndTime: Yup.string().when(['appointmentRemindersEnabled', 'appointmentCreatedConfirmationEnabled'], {
            is: (reminders, created) => Boolean(reminders || created), then: (schema) => schema.required(t('validation.required.value'))
                .test('appointment-reminder-time-order', t('chatbot_billing_time_order'), function (value) {
                    return !value || !this.parent.appointmentReminderStartTime || value > this.parent.appointmentReminderStartTime;
                })
        }),
        inactivityFollowUpEnabled: Yup.boolean(),
        inactivityThresholdMinutes: Yup.number().when('inactivityFollowUpEnabled', {
            is: true, then: (schema) => schema.required(t('validation.required.value')).integer().min(1)
        }),
        inactivityFollowUpIntervalMinutes: Yup.number().when('inactivityFollowUpEnabled', {
            is: true, then: (schema) => schema.required(t('validation.required.value')).integer().min(1)
        }),
        maxInactivityFollowUpAttempts: Yup.number().when('inactivityFollowUpEnabled', {
            is: true, then: (schema) => schema.required(t('validation.required.value')).integer().min(0)
        }),
        inactivityFollowUpStartTime: Yup.string().nullable()
            .test('paired-follow-up-time-window', t('chatbot_reengagement_time_window_required'), pairedTimeWindowValidation),
        inactivityFollowUpEndTime: Yup.string().nullable()
            .test('paired-follow-up-time-window', t('chatbot_reengagement_time_window_required'), pairedTimeWindowValidation),
        allowedServiceIds: Yup.array().of(Yup.number().integer()),
        allowedBranchIds: Yup.array().of(Yup.number().integer()),
        allowedEmployeeIds: Yup.array().of(Yup.number().integer()),
        allowedProductIds: Yup.array().of(Yup.number().integer()),
        allowedPlanIds: Yup.array().of(Yup.number().integer())
    });

    const formik = useFormik({
        initialValues: {
            name: '',
            description: '',
            systemPrompt: '',
            communicationStyle: 'FRIENDLY',
            productCatalogEnabled: true,
            subscriptionPlansEnabled: false,
            branchConsultationEnabled: true,
            employeeConsultationEnabled: true,
            appointmentBookingEnabled: false,
            billingRemindersEnabled: false,
            billingReminderIntervalDays: 1,
            billingReminderMaxAttempts: 1,
            billingReminderStartTime: '09:00',
            billingReminderEndTime: '20:00',
            billingReminderOptInConfirmed: false,
            birthdayMessagesEnabled: false,
            birthdayGiftEnabled: false,
            birthdayGiftDescription: '',
            birthdayOptInConfirmed: false,
            appointmentRemindersEnabled: false,
            appointmentSoonReminderEnabled: false,
            appointmentCreatedConfirmationEnabled: false,
            serviceFeedbackEnabled: false,
            appointmentReminderLeadHours: 24,
            appointmentReminderStartTime: '08:00',
            appointmentReminderEndTime: '20:00',
            inactivityFollowUpEnabled: false,
            inactivityThresholdMinutes: 30,
            inactivityFollowUpIntervalMinutes: 30,
            maxInactivityFollowUpAttempts: 2,
            inactivityFollowUpEscalateToHumanEnabled: false,
            inactivityFollowUpStartTime: '',
            inactivityFollowUpEndTime: '',
            allowedServiceIds: [],
            allowedBranchIds: [],
            allowedEmployeeIds: [],
            allowedProductIds: [],
            allowedPlanIds: []
        },
        validationSchema: formSchema,
        onSubmit: values => {
            const payload = {
                id: getId(entityData),
                name: values.name,
                description: values.description,
                systemPrompt: values.systemPrompt || null,
                communicationStyle: values.communicationStyle,
                productCatalogEnabled: Boolean(values.productCatalogEnabled),
                subscriptionPlansEnabled: Boolean(values.subscriptionPlansEnabled),
                branchConsultationEnabled: Boolean(values.branchConsultationEnabled),
                employeeConsultationEnabled: Boolean(values.employeeConsultationEnabled),
                appointmentBookingEnabled: Boolean(values.appointmentBookingEnabled),
                billingRemindersEnabled: Boolean(values.billingRemindersEnabled),
                billingReminderIntervalDays: Number(values.billingReminderIntervalDays || 1),
                billingReminderMaxAttempts: Number(values.billingReminderMaxAttempts || 1),
                billingReminderStartTime: values.billingReminderStartTime || '09:00',
                billingReminderEndTime: values.billingReminderEndTime || '20:00',
                billingReminderOptInConfirmed: Boolean(values.billingReminderOptInConfirmed),
                birthdayMessagesEnabled: Boolean(values.birthdayMessagesEnabled),
                birthdayGiftEnabled: Boolean(values.birthdayMessagesEnabled && values.birthdayGiftEnabled),
                birthdayGiftDescription: values.birthdayMessagesEnabled && values.birthdayGiftEnabled ? values.birthdayGiftDescription.trim() : null,
                birthdayOptInConfirmed: Boolean(values.birthdayOptInConfirmed),
                appointmentRemindersEnabled: Boolean(values.appointmentRemindersEnabled),
                appointmentSoonReminderEnabled: Boolean(values.appointmentSoonReminderEnabled),
                appointmentCreatedConfirmationEnabled: Boolean(values.appointmentCreatedConfirmationEnabled),
                serviceFeedbackEnabled: Boolean(values.serviceFeedbackEnabled),
                appointmentReminderLeadHours: Number(values.appointmentReminderLeadHours || 24),
                appointmentReminderStartTime: values.appointmentReminderStartTime || '08:00',
                appointmentReminderEndTime: values.appointmentReminderEndTime || '20:00',
                inactivityFollowUpEnabled: Boolean(values.inactivityFollowUpEnabled),
                inactivityThresholdMinutes: Number(values.inactivityThresholdMinutes || 30),
                inactivityFollowUpIntervalMinutes: Number(values.inactivityFollowUpIntervalMinutes || 30),
                maxInactivityFollowUpAttempts: Number(values.maxInactivityFollowUpAttempts ?? 2),
                inactivityFollowUpEscalateToHumanEnabled: Boolean(values.inactivityFollowUpEscalateToHumanEnabled),
                inactivityFollowUpStartTime: values.inactivityFollowUpStartTime || null,
                inactivityFollowUpEndTime: values.inactivityFollowUpEndTime || null,
                allowedServiceIds: values.allowedServiceIds || [],
                allowedBranchIds: values.allowedBranchIds || [],
                allowedEmployeeIds: values.allowedEmployeeIds || [],
                allowedProductIds: values.allowedProductIds || [],
                allowedPlanIds: values.allowedPlanIds || [],
                status: convertBooleanToStatus(status)
            };

            const onSuccess = (response) => {
                if (id) {
                    navigate(MenuConfig.chat_bot.bot.route);
                }
            };

            sendToApi(
                Api.whatsapp.bot,
                payload,
                onSuccess,
                ApiError,
                () => formik.setSubmitting(false),
                (response) => {
                    if (entityData == null) setEntityData(response.data);
                },
            );
        },
    });

    const onSubmit = (e) => {
        e.preventDefault();
        formik.handleSubmit();
    };

    return (
        <NewPage
            motherMenu={t('chat_bot')}
            childMenu={t('chatbot_assistant')}
            activeMenu={t('chatbot_assistant')}
            child={
                <Form
                    formik={formik}
                    status={status}
                    setStatus={setStatus}
                    entityData={entityData}
                />
            }
            setEntityData={setEntityData}
            path={Api.whatsapp.bot}
            isSubmitting={formik.isSubmitting || loadingConfiguration}
            handleSubmit={onSubmit}
            cancelRoute={MenuConfig.chat_bot.dashboard.route}
        />
    );
};

export default WhatsappBotNew;
